import { BigNumber, providers } from 'ethers';
import { getEvmRpcProvider } from '../evm/index';
import { SignTypeEnum, getGasFee, logoutCodes, sendTransaction, signMessage } from './walletApi';

const hexPrefix = (hex: string) => (hex.startsWith('0x') ? hex : `0x${hex}`);

export const getBybitCloudWalletProvider = (chainCode: string, address?: string) => {
  const provider = getEvmRpcProvider(chainCode, true);
  if (!provider) {
    return;
  }
  wrapSend(provider, chainCode.toUpperCase(), address ? [address] : []);
  return provider;
};

const wrapSend = (provider: providers.JsonRpcProvider, chainCode: string, accounts: string[] = []) => {
  const _send = provider.send.bind(provider);

  const handleLogoutError = (err: any) => {
    const code = err?.code;
    if (logoutCodes.includes(code)) {
      provider.emit('accountChanged', []);
      accounts = [];
    }
    throw err;
  };

  const bybitSend = async function (method: string, params: Array<any> = []): Promise<any> {
    if (method === 'eth_accounts' || method === 'eth_requestAccounts') {
      return accounts || [];
    }
    if (method === 'personal_sign') {
      const [msg, address] = params;
      const res = await signMessage({
        fromAddress: address,
        message: msg,
        chainCode,
        signType: SignTypeEnum.PERSONAL,
      }).catch(handleLogoutError);
      return hexPrefix(res);
    }

    if (method === 'eth_signTypedData_v4') {
      const [address, msg] = params;
      const typedData = JSON.parse(msg);
      typedData.domain.chainId = Number(typedData.domain.chainId);
      const res = await signMessage({
        fromAddress: address,
        message: JSON.stringify(typedData),
        chainCode,
        signType: SignTypeEnum.V4,
      }).catch(handleLogoutError);
      if (!res) {
        throw new Error('Invalid signature');
      }
      return hexPrefix(res);
    }

    if (method === 'eth_sendTransaction') {
      const [data] = params;
      // console.log('sendTransaction', params);
      const amount = BigNumber.from(data.value ?? '0').toString();
      const gasLimit = BigNumber.from(data.gas).toString();
      // console.log('amount', amount);
      const gasInfo = await getGasFee({
        chainId: provider.network.chainId,
        amount,
        contractAddress: '',
        fromAddress: data.from,
        toAddress: data.to,
        chainCode,
        walletType: 'CLOUD',
        txData: data.data,
        isL1GasOnly: false,
      });
      const middlePrice = gasInfo.middleGasPrice;
      return sendTransaction({
        fromAddress: data.from,
        toAddress: data.to,
        data: data.data,
        gasLimit,
        amount,
        maxPriceFee: middlePrice.maxGasPrice,
        gasPriorityFee: middlePrice.gasPriorityFee,
        chainCode,
      });
    }

    if (method === 'eth_sign') {
      throw new Error('eth_sign is never supported');
    }

    if (method === 'personal_unlockAccount') {
      throw new Error('personal_unlockAccount Not Supported');
    }
    if (method === 'wallet_addEthereumChain') {
      throw new Error('wallet_addEthereumChain Not Supported');
    }
    if (method === 'wallet_switchEthereumChain') {
      throw new Error('wallet_switchEthereumChain Not Supported');
    }
    return _send(method, params);
  };
  provider.send = bybitSend.bind(provider);
};
